/**
 * SectionHelp - Adds a contextual help button next to section titles
 *
 * Clicking the help icon opens the sliding help panel on the right side.
 *
 * @example
 * // Basic usage - wraps children and adds help button
 * <SectionHelp contentId="dashboard:quick-links">
 *   <h2>Quick Links</h2>
 * </SectionHelp>
 *
 * @example
 * // With custom title prop (no children needed)
 * <SectionHelp
 *   title="Team Visibility"
 *   contentId="projects:team-visibility"
 *   titleClassName="text-lg font-semibold"
 * />
 */

import { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHelp } from './useHelp';

interface SectionHelpProps {
  /** Content ID to show in help panel (e.g., "dashboard:quick-links") */
  contentId: string;
  /** Section title - if provided, renders as text. Otherwise wrap children. */
  title?: string;
  /** Custom className for the title when using title prop */
  titleClassName?: string;
  /** Children to wrap (typically a heading element) */
  children?: ReactNode;
  /** Tooltip text for the help button (shown on hover via title attribute) */
  tooltip?: string;
  /** Size of the help icon */
  size?: 'sm' | 'default';
  /** Position of help icon relative to title */
  iconPosition?: 'right' | 'left';
  /** Additional wrapper className */
  className?: string;
  /** Hide the help icon (useful for conditional display) */
  hideIcon?: boolean;
}

export function SectionHelp({
  contentId,
  title,
  titleClassName,
  children,
  tooltip = 'Get help',
  size = 'sm',
  iconPosition = 'right',
  className,
  hideIcon = false,
}: SectionHelpProps) {
  const { openHelp } = useHelp();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openHelp(contentId);
  };

  const iconSizes = {
    sm: 'h-3.5 w-3.5',
    default: 'h-4 w-4',
  };

  const buttonSizes = {
    sm: 'h-5 w-5',
    default: 'h-6 w-6',
  };

  const helpButton = !hideIcon && (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      className={cn(
        'inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary-600 hover:bg-primary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
        buttonSizes[size]
      )}
      aria-label={tooltip}
    >
      <HelpCircle className={iconSizes[size]} />
    </button>
  );

  // If title prop is provided, render it directly
  if (title) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        {iconPosition === 'left' && helpButton}
        <span className={titleClassName}>{title}</span>
        {iconPosition === 'right' && helpButton}
      </div>
    );
  }

  // Otherwise wrap children
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {iconPosition === 'left' && helpButton}
      {children}
      {iconPosition === 'right' && helpButton}
    </div>
  );
}
